import { Modal, Box, Typography, IconButton, Button, Popover } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import modalStyles from "./ChartErrorModal.styles";
import { ChartDataError } from "store/slice/charts";
import useAppziWidget from "utils/hooks/useAppziWidget";
import { useState } from "react";

type TChartErrorModal = {
    open: boolean;
    onClose: () => void;
    containerRef: React.RefObject<HTMLDivElement> | null;
};

export const ChartLSPMScoreError = ({ errorData }: { errorData: ChartDataError[] }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "error-popover" : undefined;

    return (
        <Box
            id="chart-error-text"
            sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                position: "absolute",
                bottom: -10,
                right: 20,
                padding: "5px",
                color: theme.palette.warning.main,
                zIndex: 10,
            })}
        >
            <Typography
                variant="subtitle2"
                sx={{ marginRight: 1 }}
            >{`${errorData.length} LSPM scores unavailable`}</Typography>
            <Box
                aria-describedby={id}
                onMouseEnter={handlePopoverOpen}
                sx={{
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <InfoOutlinedIcon
                    sx={(theme) => ({
                        transform: "rotate(180deg)",
                        cursor: "pointer !important",
                        color: theme.palette.warning.main,
                        zIndex: 10,
                    })}
                />
            </Box>
            <ChartErrorPopover
                id="error-popover"
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                errorData={errorData}
            />
        </Box>
    );
};

export const ChartErrorPopover = ({
    id,
    open,
    anchorEl,
    onClose,
    errorData,
}: {
    id: string;
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    errorData: ChartDataError[];
}) => {
    const classes = modalStyles();
    const intl = useIntl();

    const { handleOpenWidget } = useAppziWidget();

    return (
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
            sx={{
                padding: 2,
                zIndex: 1000,
                "& .MuiPaper-root": {
                    width: 497,
                },
            }}
            disableScrollLock
        >
            <Box className={classes.tooltipWrapper}>
                <Box className={classes.tooltipTitle}>
                    <Box display="flex" alignItems="center">
                        <InfoOutlinedIcon sx={{ rotate: "180deg", marginRight: "8px" }} />
                        <Typography variant="h6">{`${errorData.length} LSPM scores unavailable`}</Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ padding: 0, color: "#1F2124" }}>
                        <ClearOutlinedIcon />
                    </IconButton>
                </Box>
                <Box className={classes.popoverContent}>
                    <Typography variant="body1">
                        {intl.formatMessage({ id: "charts.popover.noLSPM.tooltip.content1" })}
                    </Typography>
                    <ul>
                        {errorData.map((error) => (
                            <li key={error.person_id || error.company_id}>
                                <Typography variant="body1">{error.person_name || error.company_name}</Typography>
                            </li>
                        ))}
                    </ul>
                    <Typography variant="body1">
                        {intl.formatMessage({ id: "charts.popover.noLSPM.tooltip.content2" })}
                    </Typography>
                </Box>
                <Box className={classes.popoverBtnContainer}>
                    <Button variant="outlined" size="small" sx={{ marginRight: "8px" }} onClick={onClose}>
                        <FormattedMessage id="charts.popover.noLSPM.button.dismiss" />
                    </Button>
                    <Button variant="contained" size="small" onClick={handleOpenWidget}>
                        <FormattedMessage id="charts.popover.noLSPM.button.getInTouch" />
                    </Button>
                </Box>
            </Box>
        </Popover>
    );
};

export const ChartErrorModal = ({ open, onClose, containerRef }: TChartErrorModal) => {
    const classes = modalStyles();
    const intl = useIntl();

    const { handleOpenWidget } = useAppziWidget();

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.popoverPaper}
            container={containerRef?.current}
            slotProps={{
                backdrop: {
                    style: {
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        backdropFilter: "blur(4px)",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Box className={classes.popoverWrapper}>
                    <Box className={classes.popoverTitle}>
                        <Box display="flex" alignItems="center">
                            <InfoOutlinedIcon sx={{ rotate: "180deg", marginRight: "8px" }} />
                            <Typography variant="h6">
                                {intl.formatMessage({ id: "charts.popover.noLSPM.title" })}
                            </Typography>
                        </Box>

                        <IconButton onClick={onClose} sx={{ padding: 0, color: "#FFFFFF" }}>
                            <ClearOutlinedIcon />
                        </IconButton>
                    </Box>
                    <Box className={classes.popoverContent}>
                        <Typography variant="body1">
                            {intl.formatMessage({ id: "charts.popover.noLSPM.content" })}
                        </Typography>
                    </Box>
                    <Box className={classes.popoverBtnContainer}>
                        <Button variant="outlined" size="small" sx={{ marginRight: "8px" }} onClick={onClose}>
                            <FormattedMessage id="charts.popover.noLSPM.button.dismiss" />
                        </Button>
                        <Button variant="contained" size="small" onClick={handleOpenWidget}>
                            <FormattedMessage id="charts.popover.noLSPM.button.getInTouch" />
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};
